import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, finalize, mergeAll, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { LoginService } from './login.service';
import { UserProfil } from './user-profile.model';
import { ConfigService, getOnOffConfigOrElse } from '../config/config.service';
import { User } from './user.model';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../../../environments/environment';
import { Events } from '../events/events.provider';
import { CRTDroitsEnum } from 'src/app/crt/models/crtDroit';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { UserType } from '../../../api/models/user-type';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  loginService: LoginService = inject(LoginService);
  configService: ConfigService = inject(ConfigService);
  router: Router = inject(Router);
  events: Events = inject(Events);
  authenticated: boolean = false;
  adfsUser: User | null;
  backendUser: User | null;
  isLoading: boolean = true;

  readonly MESSAGE: { NO_CONNECTION: string; UNKNOWN_ERROR: string } = {
    NO_CONNECTION: `Veuillez vérifier que le terminal est bien connecté au réseau et réessayer ultérieurement.`,
    UNKNOWN_ERROR: `Authentification : Une erreur c'est produite, merci de contacter le service technique.`,
  };

  constructor() {}

  loginToBackendFromAdfs(givenLogin: string) {
    this.isLoading = true;
    return this.loginService.login(givenLogin).pipe(
      catchError(() => {
        this.router.navigate(['/login']).then();
        return EMPTY;
      }),
      tap((userInfo) => {
        this.authenticated = true;
        this.adfsUser = UserService.toUser(userInfo);
      }),
      tap((userInfo: UserType) => {
        if (environment.datadog.rumEnabled) {
          datadogRum.setUser({
            id: userInfo.id,
            email: userInfo.email,
            name: userInfo.matricule,
          });
        }
        const isAdmin = this.isAdmin(this.adfsUser);
        if (!isAdmin) {
          this.backendUser = this.adfsUser;
        }
        return userInfo;
      }),
      switchMap(async (userInfo: UserType) => {
        const isAdmin = this.isAdmin(this.adfsUser);
        if (!isAdmin) {
          return this.impersonate(userInfo.email, false);
        }
        return of(userInfo);
      }),
      mergeAll(),
      finalize(() => (this.isLoading = false))
    );
  }

  getCurrentUser(): User | null {
    return this.backendUser;
  }

  /**
   * Utilisateur par défaut
   */
  public getDefaultUserEmail() {
    return this.backendUser?.email;
  }

  /**
   * Matricule de l'utilisateur actuel
   * @returns {string}
   */
  getMatricule(): string {
    return this.backendUser?.matricule;
  }

  /**
   * DkCodeRessource du technicien
   */
  getDkCodeRessource(): string {
    return this.backendUser?.dkCodeRessource;
  }

  /**
   * IdAdfs de l'utilisateur actuel
   * @returns {string}
   */
  getLogin(): string {
    return this.backendUser?.idadfs;
  }

  /**
   * Id de l'utilisateur actuel
   * @returns {string}
   */
  getId(): string {
    return this.backendUser?.id;
  }

  /**
   * Id de l'utilisateur actuel
   * @returns {string}
   */
  getEmail(): string {
    return this.backendUser?.email;
  }

  getCompleteName(): string {
    return this.backendUser?.prenom + ' ' + this.backendUser?.nom;
  }

  /**
   * Est en profile Lecture - Ecriture
   * @returns {boolean}
   */
  isProfilLectureEcriture(): boolean {
    return (
      this.backendUser &&
      !this.isImpersonate() &&
      this.backendUser.profils &&
      this.backendUser.profils.some((role) => role === UserProfil.CRTLectureEcriture)
    );
  }

  /**
   * Est en profile IoT
   */
  isProfilIoT(): boolean {
    return (
      this.backendUser && this.backendUser.profils && this.backendUser.profils.some((role) => role === UserProfil.IoT)
    );
  }

  isImpersonate() {
    return this.adfsUser && this.backendUser && this.adfsUser.id !== this.backendUser.id;
  }

  /**
   * Est en profile Lecture
   * @returns {boolean}
   */
  isProfilLecture(): boolean {
    return (
      this.backendUser &&
      this.backendUser.profils &&
      ((this.isImpersonate() && this.backendUser.profils.some((role) => role === UserProfil.CRTLectureEcriture)) ||
        this.backendUser.profils.some((role) => role === UserProfil.CRTLecture))
    );
  }

  /**
   * A des droits d'accès à CRT
   * @returns {boolean}
   */
  public hasCRTRights(): boolean {
    if (!Capacitor.isNativePlatform() && environment.production) {
      return false;
    }
    return this.isProfilLecture() || this.isProfilLectureEcriture();
  }

  /**
   * A des droits d'accès à IoT
   * @returns {boolean}
   */
  public hasIoTRights(): boolean {
    if (!Capacitor.isNativePlatform() && environment.production) {
      return false;
    }
    return this.isProfilIoT();
  }

  /**
   * A des droits d'accès Maintenance multi-technique
   */
  isMntProfil() {
    return (
      this.backendUser &&
      this.backendUser.profils &&
      this.backendUser.profils.some((profil) => profil === UserProfil.Mnt)
    );
  }

  /**
   * A des droits d'accès Maintenance diffus
   */
  isDiffusProfil() {
    return (
      this.backendUser &&
      this.backendUser.profils &&
      this.backendUser.profils.some((profil) => profil === UserProfil.Diffus)
    );
  }

  /**
   * A des droits d'accès SuperAdmin
   */
  isSuperAdmin(): boolean {
    return this.backendUser?.isSuperAdmin() ?? false;
  }

  logoutBackendUser() {
    this.backendUser = null;
  }

  /**
   * Définit si l'utilisateur est externe à Dalkia
   * Si c'est un prestataire alors il est externe
   */
  isUserExterne(): boolean {
    return this.backendUser.categorie == 'prestataire';
  }

  /**
   * Récupere la liste des profils de l'utilisateurs
   * @returns {string[]}
   */
  getProfils(): UserProfil[] {
    return this.backendUser ? this.backendUser.profils || [] : [];
  }

  private static toUser(loggedInUser: UserType): User {
    return new User(
      loggedInUser.id,
      loggedInUser.nom,
      loggedInUser.prenom,
      loggedInUser.msisdn,
      loggedInUser.profils ? (loggedInUser.profils as UserProfil[]) : null,
      loggedInUser.matricule,
      loggedInUser.email,
      loggedInUser.idadfs,
      loggedInUser.dk_code_ressource,
      loggedInUser.categorie,
      loggedInUser.dk_code_societe
    );
  }

  isAuthenticated(): boolean {
    return this.backendUser !== null && this.backendUser !== undefined;
  }

  needsImpersonate(): boolean {
    return this.isAdmin(this.adfsUser) && this.backendUser === undefined;
  }

  private isAdmin(userInfo: User) {
    return userInfo !== undefined && userInfo.profils.some((p) => p === UserProfil.Admin);
  }

  impersonate(givenLogin: string, fromAdfsUser: boolean): Observable<UserType> {
    return this.loginService.login(givenLogin).pipe(
      catchError((error) => throwError(error)),
      switchMap(async (userInfo) => {
        this.backendUser = UserService.toUser(userInfo);

        await this.configService.syncConfig(this.backendUser.id);
        return userInfo;
      }),
      tap((userInfo: UserType) => {
        if (environment.datadog.rumEnabled) {
          datadogRum.setUser({
            id: userInfo.id,
            email: userInfo.email,
            name: userInfo.matricule,
          });
        }
        if (this.isDatadogLogsToggleEnabled()) {
          datadogLogs.logger.setHandler('http');
          datadogLogs.logger.setContext({
            userId: userInfo.id,
            app: 'dte01',
          });
        }
        this.events.publish('authentification:firstLoginSuccessful');
        this.events.publish('refreshTab');
      })
    );
  }
  private isDatadogLogsToggleEnabled(): boolean {
    return getOnOffConfigOrElse('TOGGLE_DATADOG_LOGS', 'OFF') === 'ON';
  }

  isUserPasswordWillExpire(): boolean {
    //todo
    return false;
  }

  hasBlocageRights(droitsRequis) {
    return this.backendUser?.CRTdroits?.includes(droitsRequis);
  }

  setCRTRights(crtRights: CRTDroitsEnum[]) {
    if (this.backendUser) {
      this.backendUser.CRTdroits = crtRights;
    }
  }
}
